import { Field, getIn } from 'formik';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import React, { useEffect, useState } from 'react';
import {
  ArrowButton,
  ArrowDirection,
  FloatingInputVersionTwo,
  ShowIf,
  FormLayout,
} from 'src/components';
import { useIsMobile } from 'src/features/common/hooks/use-is-mobile';
import useDebounce from 'src/hooks/use-debounce';
import { getAddressDetailsFromSearchLocation } from 'src/services/location-management.service';
import { hasNoSpecialCharacters } from 'src/helpers/validation';
import { getSuburbs } from 'src/services/address-lookup.service';
import { toTitleCase } from 'src/helpers/formatting';
import { isEqualCaseInsensitive } from 'src/helpers/validation';
import './address-input.scss';
import { DownArrowBlack, UpArrowBlack } from '../../../../assets/images';

export function AddressInput({ field, form, ...props }) {
  const AUTOCOMPLETE_DELAY = 500;
  const EMPTY_STRING = '';
  const PROVINCES = [
    'Eastern Cape',
    'Free State',
    'Gauteng',
    'KwaZulu-Natal',
    'Limpopo',
    'Mpumalanga',
    'Northern Cape',
    'North West',
    'Western Cape',
  ];

  const isLightMode = !!props.isLightMode;

  const [showDetails, setShowDetails] = useState(false);

  const [addressesAutocomplete, setAddressesAutocomplete] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(EMPTY_STRING);
  const selectedAddressDebounced = useDebounce(selectedAddress, AUTOCOMPLETE_DELAY);

  const [validSuburbsForPostalCode, setValidSuburbsForPostalCode] = useState<string[]>([]);
  const [postalCode, setPostalCode] = useState<string | null>(null);
  const selectedPostalCodeDebounced = useDebounce(postalCode, AUTOCOMPLETE_DELAY);
  const [suburbUnvalidated, setSuburbUnvalidated] = useState<string | null>(null);

  const isMobile = useIsMobile();

  const SUB_FIELD_NAMES = {
    STREET: 'street',
    BUILDING: 'building',
    SUBURB: 'suburb',
    CITY: 'city',
    PROVINCE: 'province',
    POSTAL_CODE: 'postalCode',
  };

  const getSubfieldNameWithFieldName = (subfieldName) => `${field.name}.${subfieldName}`;

  const getMatchingValidSuburb = (suburbUnvalidated, validSuburbs) => {
    if (suburbUnvalidated) {
      return validSuburbs.find((validSuburb) =>
        isEqualCaseInsensitive(validSuburb, suburbUnvalidated),
      );
    }
    return null;
  };

  const getFieldError = (fieldName) =>
    getIn(form?.errors || {}, getSubfieldNameWithFieldName(fieldName));

  const setFieldError = (fieldName, errorMessage) =>
    form.setFieldError(getSubfieldNameWithFieldName(fieldName), errorMessage);

  const getFieldValue = (fieldName) =>
    getIn(form?.values || {}, getSubfieldNameWithFieldName(fieldName));

  const isValidPostalCodeFormFieldValue = () => !getFieldError(SUB_FIELD_NAMES.POSTAL_CODE);

  const setSuburbFormFieldValue = (suburb) => {
    const fieldValueNew = {
      ...field.value,
      [SUB_FIELD_NAMES.SUBURB]: suburb,
    };

    setShowDetails(isAllAddressDetailsEntered(fieldValueNew));
    form.setFieldValue(field.name, fieldValueNew);
  };

  useEffect(() => {
    const abortController = new AbortController();

    const fetchAutocomplete = async () => {
      // the the location autocomplete service is down, show all the address fields
      const result = await getAddressDetailsFromSearchLocation(
        selectedAddress,
        5,
        // @ts-ignore
        abortController,
        () => setShowDetails(true),
        undefined,
      );

      if (result) {
        setAddressesAutocomplete(result);
      }
    };

    if (selectedAddress && hasNoSpecialCharacters(selectedAddress)) {
      fetchAutocomplete();

      return () => {
        abortController.abort();
      };
    }
  }, [selectedAddressDebounced]);

  const initialiseSuburbs = async (postalCode, abortController) => {
    const suburbsMatchedOnPostalCodeNew = await getSuburbs(undefined, postalCode, abortController);

    if (suburbsMatchedOnPostalCodeNew && suburbsMatchedOnPostalCodeNew.length > 0) {
      setValidSuburbsForPostalCode(
        suburbsMatchedOnPostalCodeNew.map((suburb) => toTitleCase(suburb)),
      );
    } else {
      setValidSuburbsForPostalCode([]);

      if (props?.prefetch) {
        form.setFieldTouched(getSubfieldNameWithFieldName(SUB_FIELD_NAMES.POSTAL_CODE), true);
        setFieldError(SUB_FIELD_NAMES.POSTAL_CODE, 'The postal code entered is invalid');
      }
    }
  };

  useEffect(() => {
    if (props?.prefetch) {
      const zip = getIn(form.values, getSubfieldNameWithFieldName(SUB_FIELD_NAMES.POSTAL_CODE));

      if (zip) {
        setPostalCode(zip);
      }
    }
  }, []);

  useEffect(() => {
    if (postalCode && isValidPostalCodeFormFieldValue()) {
      const abortController = new AbortController();
      initialiseSuburbs(postalCode, abortController);
      return () => {
        abortController.abort();
      };
    }
  }, [selectedPostalCodeDebounced]);

  useEffect(() => {
    if (suburbUnvalidated) {
      const matchingValidSuburb = getMatchingValidSuburb(
        suburbUnvalidated,
        validSuburbsForPostalCode,
      );
      if (matchingValidSuburb) {
        setSuburbFormFieldValue(matchingValidSuburb);
        setSuburbUnvalidated(null);
      } else {
        form.setFieldValue(getSubfieldNameWithFieldName(SUB_FIELD_NAMES.SUBURB), EMPTY_STRING);
        setShowDetails(true);
      }
    }
  }, [suburbUnvalidated, validSuburbsForPostalCode]);

  const isFieldLocked = (fieldName) =>
    getIn(form?.initialStatus?.initialDisabled || {}, `${field.name}.${fieldName}`);

  const isAnyFieldsLocked = () =>
    isFieldLocked(SUB_FIELD_NAMES.STREET) ||
    isFieldLocked(SUB_FIELD_NAMES.SUBURB) ||
    isFieldLocked(SUB_FIELD_NAMES.PROVINCE) ||
    isFieldLocked(SUB_FIELD_NAMES.POSTAL_CODE);

  const areAllFieldsLocked = () =>
    isFieldLocked(SUB_FIELD_NAMES.STREET) &&
    isFieldLocked(SUB_FIELD_NAMES.SUBURB) &&
    isFieldLocked(SUB_FIELD_NAMES.PROVINCE) &&
    isFieldLocked(SUB_FIELD_NAMES.POSTAL_CODE);

  useEffect(() => {
    setShowDetails(isAnyFieldsLocked() && !areAllFieldsLocked() ? true : showDetails);
  }, [field.value]);

  function isAllAddressDetailsEntered(values, checkSuburb = true) {
    const invalidSuburb = checkSuburb && !values?.suburb;
    const invalidCity = !values?.city;
    const invalidProvince = !values?.province;
    const invalidPostalCode = !values?.postalCode;
    return invalidSuburb || invalidCity || invalidProvince || invalidPostalCode;
  }

  const onStreetAddressAutocompleteItemSelected = (item) => {
    if (item) {
      if (props.onAddressSelect) {
        props.onAddressSelect();
      }

      const selectedItem = item || {};
      const postalCode = selectedItem.address?.postalCode;
      const suburb = selectedItem.address?.suburb;

      if (suburb) {
        setSuburbUnvalidated(suburb);
      }

      if (postalCode) {
        setPostalCode(postalCode);
      }

      const newValues = {
        [SUB_FIELD_NAMES.STREET]: selectedItem.address?.streetNumber
          ? `${selectedItem.address?.streetNumber} ${selectedItem.address?.street}`
          : selectedItem.address?.street,
        [SUB_FIELD_NAMES.BUILDING]: !!selectedItem.address?.building
          ? `${selectedItem.address?.unit || EMPTY_STRING} ${
              selectedItem.address?.building || EMPTY_STRING
            }`
          : EMPTY_STRING,
        [SUB_FIELD_NAMES.CITY]: selectedItem.address?.city || EMPTY_STRING,
        [SUB_FIELD_NAMES.PROVINCE]: selectedItem.address?.province || EMPTY_STRING,
        [SUB_FIELD_NAMES.POSTAL_CODE]: postalCode || EMPTY_STRING,
        autocompletedUsedOnce: true,
      };

      setShowDetails(isAllAddressDetailsEntered(newValues, false));

      form.setFieldValue(field.name, newValues);
      return {
        ...form.values,
        [field.name]: newValues,
      };
    } else if (field?.value?.autocompletedUsedOnce) {
      setShowDetails(true);
    }
  };

  const onPostalCodeChanged = (e) => {
    setPostalCode(e.target.value);
    form.setFieldValue(getSubfieldNameWithFieldName(SUB_FIELD_NAMES.SUBURB), EMPTY_STRING);
    form.handleChange(e);
  };

  const onSuburbClicked = () => {
    if (validSuburbsForPostalCode.length === 0 && getFieldValue(SUB_FIELD_NAMES.SUBURB)) {
      initialiseSuburbs(getFieldValue(SUB_FIELD_NAMES.POSTAL_CODE), null);
    }
  };

  return (
    <div className="AddressInput">
      {isAnyFieldsLocked() ? (
        <Field
          name={getSubfieldNameWithFieldName(SUB_FIELD_NAMES.STREET)}
          type="text"
          placeholder="Street address"
          component={MiniAppFocusWrapper}
          innerComponent={props.innerComponent || FloatingInputVersionTwo}
          fieldType="standard"
          className={isLightMode ? '' : 'dark'}
          automationid="Onboarding_Create_Account_Your_Business_Field_Street"
          subtext="Eg. 12 Ridge Street"
        />
      ) : (
        <Field
          name={getSubfieldNameWithFieldName(SUB_FIELD_NAMES.STREET)}
          type="text"
          placeholder="Street address"
          component={MiniAppFocusWrapper}
          innerComponent={props.innerComponent || FloatingInputVersionTwo}
          items={addressesAutocomplete}
          getItemDisplayValue={(item) => item.addressName}
          onInputValueChange={(newValue) => setSelectedAddress(newValue)}
          onItemSelect={onStreetAddressAutocompleteItemSelected}
          fieldType="autocomplete"
          automationIdPrefix="Onboarding_Create_Account_Your_Business"
          isDark={!isLightMode}
          styleIconOpen="icon-location-icon"
          subtext="Eg. 12 Ridge Street"
          autoFocus={!isMobile}
        />
      )}

      <div className={`show-all-fields-button-container ${isLightMode ? 'is-light' : ''}`}>
        <ArrowButton
          text={`${showDetails ? 'Hide' : 'Show'} ${props.titleLowerCase} details`}
          arrowDirection={showDetails ? ArrowDirection.Up : ArrowDirection.Down}
          onClick={() => {
            if (props.onShowBusinessAddress) {
              props.onShowBusinessAddress();
            }
            setShowDetails((currentValue) => !currentValue);
          }}
          automationId={`${props.automationPageName}_Arrow_Button_Show_Address_Details`}
          arrowDownIcon={isLightMode ? DownArrowBlack : undefined}
          arrowUpIcon={isLightMode ? UpArrowBlack : undefined}
          style="content-normal"
        />
      </div>

      <ShowIf condition={showDetails}>
        <FormLayout>
          <Field
            name={getSubfieldNameWithFieldName(SUB_FIELD_NAMES.BUILDING)}
            type="text"
            placeholder="Complex / Building (Optional)"
            subtext="Complex, building or mall name, unit or shop number"
            component={MiniAppFocusWrapper}
            innerComponent={props.innerComponent || FloatingInputVersionTwo}
            fieldType="standard"
            className={isLightMode ? 'light' : 'dark'}
            automationid={`${props.automationPageName}_Field_Building`}
          />
          <Field
            name={getSubfieldNameWithFieldName(SUB_FIELD_NAMES.POSTAL_CODE)}
            type="text"
            maxLength="4"
            placeholder="Postal code"
            component={MiniAppFocusWrapper}
            innerComponent={props.innerComponent || FloatingInputVersionTwo}
            lightMode={true}
            fieldType="standard"
            onChange={onPostalCodeChanged}
            className={isLightMode ? 'light bottom-field' : 'dark  bottom-field'}
            automationid="Onboarding_Create_Account_Your_Business_Field_Postal_Code"
          />
          <Field
            name={getSubfieldNameWithFieldName(SUB_FIELD_NAMES.SUBURB)}
            type="text"
            placeholder="Suburb"
            items={validSuburbsForPostalCode}
            getItemDisplayValue={(item) => item}
            component={MiniAppFocusWrapper}
            innerComponent={props.innerComponent || FloatingInputVersionTwo}
            fieldType="select"
            onClick={onSuburbClicked}
            isDark={!isLightMode}
            className={isLightMode ? 'light' : 'dark'}
            automationIdPrefix="Onboarding_Create_Account_Your_Business_Field_Suburb"
          />
          <Field
            name={getSubfieldNameWithFieldName(SUB_FIELD_NAMES.CITY)}
            type="text"
            placeholder="City / Town"
            component={MiniAppFocusWrapper}
            innerComponent={props.innerComponent || FloatingInputVersionTwo}
            fieldType="standard"
            className={isLightMode ? 'light' : 'dark'}
            automationid="Onboarding_Create_Account_Your_Business_Field_City"
          />
          <Field
            name={getSubfieldNameWithFieldName(SUB_FIELD_NAMES.PROVINCE)}
            type="text"
            placeholder="Province"
            items={PROVINCES}
            getItemDisplayValue={(item) => item}
            component={MiniAppFocusWrapper}
            innerComponent={props.innerComponent || FloatingInputVersionTwo}
            fieldType="select"
            isDark={!isLightMode}
            className={isLightMode ? 'light' : 'dark'}
            automationIdPrefix="Onboarding_Create_Account_Your_Business_Field_Province"
          />
        </FormLayout>
        <div className="padding-bottom" />
      </ShowIf>
    </div>
  );
}
